import { apiClient, ApiResponse } from './apiClient';

export interface Coupon {
  id: number;
  code: string;
  type: 'percentage' | 'amount' | 'fixed' | 'flat';
  value: number;
  start_date: string;
  end_date: string;
  min_order_amount?: number;
  usage_limit?: number;
  used_count: number;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

export interface CouponFilters {
  search?: string;
  type?: 'percentage' | 'amount' | 'fixed' | 'flat';
  status?: 'active' | 'inactive';
  page?: number;
  limit?: number;
}

export interface CouponListResponse {
  coupons: Coupon[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateCouponRequest {
  code: string;
  type: 'percentage' | 'amount' | 'fixed' | 'flat';
  value: number;
  start_date: string;
  end_date: string;
  min_order_amount?: number;
  usage_limit?: number;
  status: 'active' | 'inactive';
}

export interface UpdateCouponRequest extends Partial<CreateCouponRequest> {
  id: number;
}

export class CouponsService {
  private baseUrl = '/admin/coupons';

  // Get all coupons with filters
  async getCoupons(filters: CouponFilters = {}): Promise<ApiResponse<CouponListResponse>> {
    try {
      console.log('🔧 CouponsService.getCoupons called:', { filters });
      
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const queryString = params.toString();
      const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
      
      console.log('🔧 Making GET request to:', url);
      console.log('🔧 Query parameters:', Object.fromEntries(params));
      
      const response = await apiClient.get<Coupon[]>(url);
      
      console.log('🔧 CouponsService.getCoupons response:', response);
      console.log('🔧 Response success:', response.success);
      console.log('🔧 Response data type:', typeof response.data);
      console.log('🔧 Response data:', response.data);
      
      if (response.success && response.data) {
        // Handle different response formats
        let coupons: Coupon[] = [];
        let total = 0;
        let page = filters.page || 1;
        let limit = filters.limit || 10;
        
        console.log('🔧 Processing response data:', response.data);
        console.log('🔧 Is array?', Array.isArray(response.data));
        console.log('🔧 Has coupons property?', !!(response.data as any).coupons);
        
        if (Array.isArray(response.data)) {
          // Direct array response
          console.log('🔧 Using direct array format');
          coupons = response.data;
          total = coupons.length;
        } else if ((response.data as any).coupons) {
          // Paginated response
          console.log('🔧 Using paginated format');
          const paginatedData = response.data as any;
          coupons = paginatedData.coupons || [];
          total = paginatedData.total || coupons.length;
          page = paginatedData.page || page;
          limit = paginatedData.limit || limit;
        } else {
          // Try to extract from nested data structure
          console.log('🔧 Trying nested data extraction');
          const nestedData = (response.data as any).data;
          if (Array.isArray(nestedData)) {
            coupons = nestedData;
            total = coupons.length;
          } else if (nestedData && nestedData.coupons) {
            coupons = nestedData.coupons || [];
            total = nestedData.total || coupons.length;
            page = nestedData.page || page;
            limit = nestedData.limit || limit;
          }
        }
        
        console.log('🔧 Final extracted coupons:', coupons);
        console.log('🔧 Final coupons count:', coupons.length);
        
        const totalPages = Math.ceil(total / limit);
        
        const listResponse: CouponListResponse = {
          coupons,
          total,
          page,
          limit,
          totalPages
        };
        
        return {
          success: true,
          data: listResponse,
          message: 'Coupons retrieved successfully'
        };
      }
      
      return {
        success: false,
        data: {
          coupons: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        },
        message: 'Error fetching coupons'
      };
    } catch (error: any) {
      console.error('🔧 CouponsService.getCoupons error:', error);
      
      return {
        success: false,
        data: {
          coupons: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        },
        message: 'Error fetching coupons'
      };
    }
  }

  // Get single coupon by ID
  async getCoupon(id: number): Promise<ApiResponse<Coupon>> {
    try {
      console.log('🔧 CouponsService.getCoupon called:', { id });
      
      const response = await apiClient.get<Coupon>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 CouponsService.getCoupon response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 CouponsService.getCoupon error:', error);
      
      return {
        success: false,
        data: null as any,
        message: 'Error fetching coupon'
      };
    }
  }

  // Create new coupon
  async createCoupon(data: CreateCouponRequest): Promise<ApiResponse<Coupon>> {
    try {
      console.log('🔧 CouponsService.createCoupon called:', { data });
      
      // Ensure dates are in ISO format
      const apiData = {
        ...data,
        start_date: data.start_date ? new Date(data.start_date).toISOString() : new Date().toISOString(),
        end_date: data.end_date ? new Date(data.end_date).toISOString() : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        value: Number(data.value),
        min_order_amount: data.min_order_amount ? Number(data.min_order_amount) : undefined,
        usage_limit: data.usage_limit ? Number(data.usage_limit) : undefined,
      };
      
      console.log('🔧 CouponsService.createCoupon prepared data:', apiData);
      console.log('🔧 Type value being sent:', apiData.type);
      console.log('🔧 Supported types: percentage, fixed, amount');
      console.log('🔧 Full request body:', JSON.stringify(apiData, null, 2));
      
      const response = await apiClient.post<Coupon>(this.baseUrl, apiData);
      
      console.log('🔧 CouponsService.createCoupon response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 CouponsService.createCoupon error:', error);
      console.error('🔧 Error response data:', error.response?.data);
      console.error('🔧 Error response status:', error.response?.status);
      
      let errorMessage = 'Error creating coupon';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Handle validation errors array
        const validationErrors = error.response.data.errors.map((err: any) => {
          if (typeof err === 'string') return err;
          if (err.msg && err.path) return `${err.path}: ${err.msg}`;
          return err.msg || err.message || 'Validation error';
        });
        errorMessage = validationErrors.join(', ');
        
        // Special handling for type validation error
        const typeError = error.response.data.errors.find((err: any) => err.path === 'type');
        if (typeError) {
          console.error('🔧 Type validation error detected. Backend expects different values than "percentage" and "amount"');
          console.error('🔧 Current value sent:', typeError.value);
          console.error('🔧 Error message:', typeError.msg);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage,
        errors: error.response?.data?.errors || []
      };
    }
  }

  // Update existing coupon
  async updateCoupon(id: number, data: Partial<CreateCouponRequest>): Promise<ApiResponse<Coupon>> {
    try {
      console.log('🔧 CouponsService.updateCoupon called:', { id, data });
      
      // Prepare data for API - send all fields that can be updated
      const updateData: any = {};
      
      // Include all core fields (as per your API example)
      if (data.code !== undefined && data.code !== null && data.code.trim() !== '') {
        updateData.code = data.code.trim();
        console.log('🔧 Adding code:', updateData.code);
      }
      if (data.type !== undefined && data.type !== null) {
        updateData.type = data.type;
        console.log('🔧 Adding type:', updateData.type);
      }
      if (data.value !== undefined && data.value !== null) {
        updateData.value = Number(data.value);
        console.log('🔧 Adding value:', updateData.value);
      }
      if (data.min_order_amount !== undefined && data.min_order_amount !== null) {
        updateData.min_order_amount = Number(data.min_order_amount);
        console.log('🔧 Adding min_order_amount:', updateData.min_order_amount);
      }
      if (data.usage_limit !== undefined && data.usage_limit !== null) {
        updateData.usage_limit = Number(data.usage_limit);
        console.log('🔧 Adding usage_limit:', updateData.usage_limit);
      }
      if (data.status !== undefined && data.status !== null) {
        updateData.status = data.status;
        console.log('🔧 Adding status:', updateData.status);
      }
      
      // Date fields
      if (data.start_date !== undefined && data.start_date !== null && data.start_date !== '') {
        updateData.start_date = new Date(data.start_date).toISOString();
        console.log('🔧 Adding start_date:', updateData.start_date);
      }
      if (data.end_date !== undefined && data.end_date !== null && data.end_date !== '') {
        updateData.end_date = new Date(data.end_date).toISOString();
        console.log('🔧 Adding end_date:', updateData.end_date);
      }
      
      console.log('🔧 CouponsService.updateCoupon prepared data (PATCH):', updateData);
      console.log('🔧 Making PATCH request to:', `${this.baseUrl}/${id}`);
      console.log('🔧 Request payload:', JSON.stringify(updateData, null, 2));
      
      // Check if we have any data to send
      if (Object.keys(updateData).length === 0) {
        console.warn('🔧 WARNING: No data to update! updateData is empty');
        return {
          success: false,
          data: null as any,
          message: 'No data provided for update'
        };
      }
      
      const response = await apiClient.patch<Coupon>(`${this.baseUrl}/${id}`, updateData);
      
      console.log('🔧 CouponsService.updateCoupon response:', response);
      console.log('🔧 Response status:', response.success);
      console.log('🔧 Response data:', response.data);
      console.log('🔧 Response message:', response.message);
      
      return response;
    } catch (error: any) {
      console.error('🔧 CouponsService.updateCoupon error:', error);
      console.error('🔧 Error response data:', error.response?.data);
      console.error('🔧 Error response status:', error.response?.status);
      
      let errorMessage = 'Error updating coupon';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Handle validation errors array
        const validationErrors = error.response.data.errors.map((err: any) => {
          if (typeof err === 'string') return err;
          if (err.msg && err.path) return `${err.path}: ${err.msg}`;
          return err.msg || err.message || 'Validation error';
        });
        errorMessage = validationErrors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage,
        errors: error.response?.data?.errors || []
      };
    }
  }

  // Delete coupon
  async deleteCoupon(id: number): Promise<ApiResponse<void>> {
    try {
      console.log('🔧 CouponsService.deleteCoupon called:', { id });
      
      const response = await apiClient.delete<void>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 CouponsService.deleteCoupon response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 CouponsService.deleteCoupon error:', error);
      
      let errorMessage = 'Error deleting coupon';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: undefined,
        message: errorMessage
      };
    }
  }

  // Toggle coupon status (activate/deactivate)
  async toggleCouponStatus(id: number, status: 'active' | 'inactive'): Promise<ApiResponse<Coupon>> {
    try {
      console.log('🔧 CouponsService.toggleCouponStatus called:', { id, status });
      
      const response = await apiClient.patch<Coupon>(`${this.baseUrl}/${id}/status`, { status });
      
      console.log('🔧 CouponsService.toggleCouponStatus response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 CouponsService.toggleCouponStatus error:', error);
      
      return {
        success: false,
        data: null as any,
        message: 'Error updating coupon status'
      };
    }
  }

  // Note: getCouponStats method removed - stats are calculated from coupons data in the component
}

export const couponsService = new CouponsService();
